/**
 * Data layer for `per-publisher-fraud-rule-sets`. Thin typed fetch wrappers over /api/v1/per-publisher-fraud-rule-sets.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface PerPublisherFraudRuleSetsRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListPerPublisherFraudRuleSetsResponse {
	count: number;
	items: PerPublisherFraudRuleSetsRow[];
}

export interface CreatePerPublisherFraudRuleSetsBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/per-publisher-fraud-rule-sets";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListPerPublisherFraudRuleSetsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListPerPublisherFraudRuleSetsResponse>(res);
}

export async function create(body: CreatePerPublisherFraudRuleSetsBody): Promise<PerPublisherFraudRuleSetsRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<PerPublisherFraudRuleSetsRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const perPublisherFraudRuleSetsQueryKeys = {
	all: ["per-publisher-fraud-rule-sets"] as const,
	list: () => [...perPublisherFraudRuleSetsQueryKeys.all] as const,
	detail: (id: string) => [...perPublisherFraudRuleSetsQueryKeys.all, id] as const,
};
